import React from 'react';
import { useState, useEffect } from 'react';
import { api } from 'src/api/client';
import { useToast } from 'src/Contexts/ToastContext';
import './AuthenticatedImage.css';

interface AuthenticatedImageProps {
  fileId: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const AuthenticatedImage = ({
  fileId,
  alt = '',
  className = '',
  style = {},
}: AuthenticatedImageProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();

  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);

        // api.get() unwraps res.data, so `blob` IS the Blob directly
        const blob: Blob = await api.get(`/api/v2/files/${fileId}`, {
          responseType: 'blob',
        });

        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
        console.error('Image load error:', err);
        toast.error('Failed to load image');
      }
    };

    void fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId, toast]);

  if (loading) {
    return (
      <div className={`authenticated-image-loading ${className}`} style={style}>
        <div className="loading-spinner" />
        <span className="sr-only">Loading image...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`authenticated-image-error ${className}`} style={style}>
        <div className="error-icon">🚫</div>
        <span className="sr-only">Failed to load image</span>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className={`authenticated-image-empty ${className}`} style={style}>
        <div className="empty-icon">📷</div>
        <span className="sr-only">No image available</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onError={() => setError(new Error('Image failed to load'))}
    />
  );
};
